import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw,
  X, Trash2, Clock, MapPin, Phone, User,
} from "lucide-react";
import { listAppointments, createAppointment, patchAppointment, deleteAppointment } from "@/lib/dashboardApi";

const TECH_ROWS = ["Todd", "Jordan", "Josh", "Adam"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameISO = (d, iso) => toISO(d) === iso;
const timeLabel = (t) => {
  if (!t) return "";
  const m = String(t).match(/^(\d{1,2}):(\d{2})/); if (!m) return t;
  let h = parseInt(m[1], 10); const min = m[2];
  const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
};
const timeVal = (t) => { const m = String(t || "").match(/^(\d{1,2}):(\d{2})/); return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : 99999; };
const TYPE_COLORS = { "Install": "#22c55e", "Repair/Service": "#a855f7", "Estimate": "#0ea5e9" };
const APPT_TYPES = ["Repair/Service", "Install", "Estimate"];
const typeColor = (t) => TYPE_COLORS[t] || "#a855f7";

const CalendarBoard = () => {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(() => new Date());
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setAppts(await listAppointments()); }
    catch { toast.error("Could not load calendar."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const byDate = useMemo(() => {
    const m = {};
    appts.forEach((a) => { const k = a.scheduled_date || ""; (m[k] = m[k] || []).push(a); });
    Object.values(m).forEach((arr) => arr.sort((x, y) => timeVal(x.scheduled_time) - timeVal(y.scheduled_time)));
    return m;
  }, [appts]);
  const monthDays = useMemo(() => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const start = new Date(first); start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [date]);
  const weekDays = useMemo(() => {
    const s = new Date(date); s.setDate(date.getDate() - date.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
  }, [date]);
  const shift = (n) => {
    const d = new Date(date);
    if (view === "month") d.setMonth(date.getMonth() + n);
    else if (view === "week") d.setDate(date.getDate() + n * 7);
    else d.setDate(date.getDate() + n);
    setDate(d);
  };
  const heading = view === "month"
    ? date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : view === "week"
      ? `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const segCls = (on) => `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${on ? "bg-[#12355B] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`;
  return (
    <div className="space-y-5" data-testid="calendar-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {["month", "week", "day"].map((v) => (
            <button key={v} onClick={() => setView(v)} className={segCls(view === v)} data-testid={`calendar-view-${v}`}>{v[0].toUpperCase() + v.slice(1)}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white min-w-[220px] justify-center">
            <CalendarDays className="w-4 h-4 text-[#F97316]" />
            <span className="text-sm font-semibold text-slate-800" data-testid="calendar-heading">{heading}</span>
          </div>
          <button onClick={() => shift(1)} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setDate(new Date())} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:border-slate-300" data-testid="calendar-today">Today</button>
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300" title="Refresh" data-testid="calendar-refresh">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
          <button onClick={() => setEditing({ scheduled_date: toISO(date) })} className="px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold flex items-center gap-1.5" data-testid="calendar-add"><Plus className="w-4 h-4" /> Add Appointment</button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500" data-testid="calendar-legend">
        {APPT_TYPES.map((t) => (
          <span key={t} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: TYPE_COLORS[t] }} />{t}</span>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : view === "month" ? (
        <MonthView days={monthDays} monthIdx={date.getMonth()} byDate={byDate} onAdd={(iso) => setEditing({ scheduled_date: iso })} onOpen={setEditing} />
      ) : view === "week" ? (
        <WeekView days={weekDays} byDate={byDate} onAdd={(iso) => setEditing({ scheduled_date: iso })} onOpen={setEditing} />
      ) : (
        <DayView iso={toISO(date)} items={byDate[toISO(date)] || []} onAdd={(iso) => setEditing({ scheduled_date: iso })} onOpen={setEditing} />
      )}
      {editing && <ApptModal appt={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const Chip = ({ a, onOpen, compact }) => (
  <button onClick={(e) => { e.stopPropagation(); onOpen(a); }}
    className="w-full text-left rounded-md px-1.5 py-1 mb-1 overflow-hidden hover:brightness-95 transition"
    style={{ backgroundColor: `${typeColor(a.appt_type)}1a`, borderLeft: `3px solid ${typeColor(a.appt_type)}` }} data-testid={`calendar-appt-${a.id}`}>
    <span className="text-[11px] font-bold text-slate-800 truncate block leading-tight">{a.scheduled_time ? timeLabel(a.scheduled_time) + " " : ""}{a.customer_name}</span>
    {!compact && <span className="text-[10px] text-slate-500 truncate block leading-tight">{a.scope || a.assigned_tech || ""}</span>}
  </button>
);

const MonthView = ({ days, monthIdx, byDate, onAdd, onOpen }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
      {WD.map((w) => <div key={w} className="px-2 py-2 text-xs font-bold text-slate-500 text-center">{w}</div>)}
    </div>
    <div className="grid grid-cols-7">
      {days.map((d, i) => {
        const iso = toISO(d);
        const items = byDate[iso] || [];
        const out = d.getMonth() !== monthIdx;
        const today = sameISO(new Date(), iso);
        return (
          <div key={i} onClick={() => onAdd(iso)} className={`min-h-[112px] border-b border-r border-slate-100 p-1.5 cursor-pointer hover:bg-slate-50/60 ${out ? "bg-slate-50/40" : ""}`} data-testid={`calendar-day-${iso}`}>
            <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${today ? "bg-[#F97316] text-white" : out ? "text-slate-300" : "text-slate-600"}`}>{d.getDate()}</div>
            {items.slice(0, 3).map((a) => <Chip key={a.id} a={a} onOpen={onOpen} compact />)}
            {items.length > 3 && <span className="text-[10px] text-slate-400 pl-1">+{items.length - 3} more</span>}
          </div>
        );
      })}
    </div>
  </div>
);

const WeekView = ({ days, byDate, onAdd, onOpen }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="grid grid-cols-7">
      {days.map((d) => {
        const iso = toISO(d); const items = byDate[iso] || []; const today = sameISO(new Date(), iso);
        return (
          <div key={iso} onClick={() => onAdd(iso)} className="min-h-[420px] border-r border-slate-100 p-2 cursor-pointer hover:bg-slate-50/50" data-testid={`calendar-week-day-${iso}`}>
            <div className="text-center mb-2">
              <p className="text-[11px] font-bold text-slate-400">{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
              <p className={`text-sm font-bold w-7 h-7 mx-auto flex items-center justify-center rounded-full ${today ? "bg-[#F97316] text-white" : "text-slate-700"}`}>{d.getDate()}</p>
            </div>
            {items.map((a) => <Chip key={a.id} a={a} onOpen={onOpen} />)}
          </div>
        );
      })}
    </div>
  </div>
);

const DayView = ({ iso, items, onAdd, onOpen }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`calendar-day-view-${iso}`}>
    {items.length === 0 ? (
      <div className="text-center py-16">
        <p className="text-sm text-slate-400 mb-3">No appointments this day.</p>
        <button onClick={() => onAdd(iso)} className="px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add Appointment</button>
      </div>
    ) : (
      <div className="space-y-2">
        {items.map((a) => (
          <button key={a.id} onClick={() => onOpen(a)} className="w-full text-left flex items-start gap-3 rounded-lg border border-slate-200 p-3 hover:shadow-sm transition" style={{ borderLeft: `4px solid ${typeColor(a.appt_type)}` }} data-testid={`calendar-appt-${a.id}`}>
            <div className="w-20 shrink-0 text-sm font-bold text-slate-700">{a.scheduled_time ? timeLabel(a.scheduled_time) : "—"}</div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">{a.customer_name}</p>
              {a.scope && <p className="text-xs text-slate-500 truncate">{a.scope}</p>}
              <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-slate-400">
                {a.assigned_tech && <span className="flex items-center gap-1"><User className="w-3 h-3" />{a.assigned_tech}</span>}
                {a.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>}
                {a.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.address}</span>}
                {a.duration_min && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.duration_min}m</span>}
              </div>
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

const ApptModal = ({ appt, onClose, onSaved }) => {
  const isEdit = !!appt.id;
  const [f, setF] = useState({
    customer_name: appt.customer_name || "", phone: appt.phone || "", address: appt.address || "",
    scope: appt.scope || "", notes: appt.notes || "", scheduled_date: appt.scheduled_date || "",
    scheduled_time: appt.scheduled_time || "09:00", duration_min: appt.duration_min || 60, assigned_tech: appt.assigned_tech || "",
    appt_type: appt.appt_type || "Repair/Service",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.customer_name.trim()) { toast.error("Name is required."); return; }
    if (!f.scheduled_date) { toast.error("Please pick a date."); return; }
    setBusy(true);
    try {
      const payload = { ...f, duration_min: Number(f.duration_min) || 60 };
      if (isEdit) await patchAppointment(appt.id, payload);
      else await createAppointment(payload);
      toast.success(isEdit ? "Appointment updated." : "Appointment added.");
      onSaved();
    } catch { toast.error("Could not save appointment."); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await deleteAppointment(appt.id); toast.success("Appointment deleted."); onSaved(); }
    catch { toast.error("Could not delete."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="calendar-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{isEdit ? "Edit Appointment" : "New Appointment"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="calendar-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <input autoFocus value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Name *" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-name" />
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone number" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-phone" />
          <input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Address" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-address" />
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Type</label>
            <div className="flex gap-2 mt-1">
              {APPT_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => set("appt_type", t)}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-semibold border transition ${f.appt_type === t ? "text-white" : "text-slate-600 bg-white border-slate-200 hover:border-slate-300"}`}
                  style={f.appt_type === t ? { backgroundColor: TYPE_COLORS[t], borderColor: TYPE_COLORS[t] } : {}}
                  data-testid={`calendar-f-type-${t.replace(/[^a-z]/gi, "").toLowerCase()}`}>{t}</button>
              ))}
            </div>
          </div>
          <textarea value={f.scope} onChange={(e) => set("scope", e.target.value)} placeholder="Scope of work" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-scope" />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Date</label>
              <input type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className="w-full px-2 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-date" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Time</label>
              <input type="time" value={f.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className="w-full px-2 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-time" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Mins</label>
              <input type="number" min="15" step="15" value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="w-full px-2 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-duration" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Technician (shows on Dispatch Board)</label>
            <select value={f.assigned_tech} onChange={(e) => set("assigned_tech", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-tech">
              <option value="">Unassigned</option>
              {TECH_ROWS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="calendar-f-notes" />
        </div>
        <div className="flex items-center justify-between mt-5">
          {isEdit ? (
            <button onClick={remove} disabled={busy} className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60" data-testid="calendar-delete"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60" data-testid="calendar-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {isEdit ? "Save" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarBoard;
